// components/BuildingLogsModal.tsx
import React, { useState } from 'react';
import { ServiceLogEntry, BuildingSummary, ServiceType } from '../types/analysis.types';
import { formatDate, formatDateTime } from '../utils/dateUtils';
import COLORS from "../../../Helpers/utils";
import ServiceLogValueCell from './ServiceLogValueCell';

interface BuildingLogsModalProps {
    isOpen: boolean;
    onClose: () => void;
    building: BuildingSummary | null;
    logEntries: ServiceLogEntry[];
    commentEntries: ServiceLogEntry[];
    individualVisits: ServiceLogEntry[];
    users: any[];
    serviceTypes: ServiceType[];
    serviceTitles: Record<string, string>;
    /** Admins may correct values of services that collect one */
    canCorrect: boolean;
    /** Called after a value was corrected, so the page reloads */
    onCorrected: () => void;
}

interface CombinedEntry extends ServiceLogEntry {
    entryType: 'service' | 'comment';
}

const BuildingLogsModal: React.FC<BuildingLogsModalProps> = ({
                                                                 isOpen,
                                                                 onClose,
                                                                 building,
                                                                 logEntries,
                                                                 commentEntries,
                                                                 individualVisits,
                                                                 users,
                                                                 serviceTypes,
                                                                 serviceTitles,
                                                                 canCorrect,
                                                                 onCorrected
                                                             }) => {
    const [activeTab, setActiveTab] = useState<'activities' | 'visits'>('activities');

    if (!isOpen || !building) return null;

    // Filter data for the selected building and combine services and comments
    const buildingLogEntries = logEntries.filter(entry => entry.buildingKey === building.key);
    const buildingComments = commentEntries.filter(entry => entry.buildingKey === building.key);
    const buildingVisits = individualVisits.filter(entry => entry.buildingKey === building.key);

    // Combine services and comments into one sorted list
    const combinedActivities: CombinedEntry[] = [
        ...buildingLogEntries.map(entry => ({ ...entry, entryType: 'service' as const })),
        ...buildingComments.map(entry => ({ ...entry, entryType: 'comment' as const }))
    ].sort((a, b) => new Date(b.dateString).getTime() - new Date(a.dateString).getTime());

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const getUserName = (userKey: string) => {
        return users ? (users.find(user => user.key === userKey)?.name || userKey) : userKey;
    };

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '20px'
            }}
            onClick={handleBackdropClick}
        >
            <div
                style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    width: '1000px',
                    maxWidth: '100%',
                    height: '80%',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
                }}
            >
                {/* Header */}
                <div style={{
                    padding: '20px',
                    borderBottom: '1px solid #eee',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexShrink: 0
                }}>
                    <h2 style={{ margin: 0, fontSize: '1.5em', color: '#333' }}>
                        Loggar för {building.name}
                    </h2>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '24px',
                            cursor: 'pointer',
                            color: '#666',
                            padding: '0',
                            width: '30px',
                            height: '30px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '50%'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                        ×
                    </button>
                </div>

                {/* Tabs */}
                <div style={{
                    display: 'flex',
                    borderBottom: '1px solid #eee',
                    backgroundColor: '#f8f9fa',
                    flexShrink: 0
                }}>
                    {[
                        { key: 'activities', label: `Aktiviteter (${combinedActivities.length})` },
                        { key: 'visits', label: `Besök (${buildingVisits.length})` }
                    ].map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key as any)}
                            style={{
                                padding: '12px 20px',
                                border: 'none',
                                background: activeTab === tab.key ? 'white' : 'transparent',
                                cursor: 'pointer',
                                borderBottom: activeTab === tab.key ? '2px solid #007bff' : '2px solid transparent',
                                color: activeTab === tab.key ? COLORS.primaryDark : '#666',
                                fontWeight: activeTab === tab.key ? 'bold' : 'normal'
                            }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Content with fixed height and scroll */}
                <div style={{
                    flex: 1,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    <div style={{
                        margin: '20px',
                        flex: 1,
                        overflow: 'auto'
                    }}>
                        {activeTab === 'activities' && (
                            <div>
                                {combinedActivities.length > 0 ? (
                                    <table style={{
                                        width: '100%',
                                        borderCollapse: 'collapse',
                                        fontSize: '14px'
                                    }}>
                                        <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                                        <tr style={{ borderBottom: '2px solid #eee' }}>
                                            <th style={{ textAlign: 'left', padding: '12px 8px', borderRight: '1px solid #f0f0f0' }}>Datum & Tid</th>
                                            <th style={{ textAlign: 'left', padding: '12px 8px', borderRight: '1px solid #f0f0f0' }}>Användare</th>
                                            <th style={{ textAlign: 'left', padding: '12px 8px', borderRight: '1px solid #f0f0f0' }}>Typ</th>
                                            <th style={{ textAlign: 'left', padding: '12px 8px', borderRight: '1px solid #f0f0f0' }}>Tjänst/Kommentar</th>
                                            <th style={{ textAlign: 'left', padding: '12px 8px' }}>Mängd</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {combinedActivities.map((entry, index) => (
                                            <tr
                                                key={`${entry.entryType}-${entry.id}`}
                                                style={{
                                                    borderBottom: '1px solid #f5f5f5',
                                                    backgroundColor: index % 2 === 1 ? '#f9f9f9' : 'transparent'
                                                }}
                                            >
                                                <td style={{ padding: '10px 8px', borderRight: '1px solid #f0f0f0' }}>
                                                    {formatDateTime(entry.dateString)}
                                                </td>
                                                <td style={{ padding: '10px 8px', borderRight: '1px solid #f0f0f0' }}>
                                                    {getUserName(entry.user)}
                                                </td>
                                                <td style={{ padding: '10px 8px', borderRight: '1px solid #f0f0f0' }}>
                                                        <span style={{
                                                            padding: '2px 6px',
                                                            borderRadius: '3px',
                                                            fontSize: '12px',
                                                            fontWeight: 'bold',
                                                            backgroundColor: entry.entryType === 'service' ? '#e3f2fd' : '#fff3e0',
                                                            color: entry.entryType === 'service' ? COLORS.primaryDark : '#f57c00'
                                                        }}>
                                                            {entry.entryType === 'service' ? 'TJÄNST' : 'KOMMENTAR'}
                                                        </span>
                                                </td>
                                                <td style={{ padding: '10px 8px', borderRight: '1px solid #f0f0f0' }}>
                                                    {entry.entryType === 'service'
                                                        ? (serviceTitles[entry.serviceType || ''] || entry.serviceType || 'Okänd')
                                                        : entry.comment
                                                    }
                                                </td>
                                                <td style={{ padding: '10px 8px' }}>
                                                    {entry.entryType === 'service' && entry.value !== undefined && (() => {
                                                        const serviceType = serviceTypes.find(st => st.key === entry.serviceType);
                                                        return (
                                                            <ServiceLogValueCell
                                                                entry={entry}
                                                                unit={serviceType?.unit}
                                                                serviceTitle={serviceTitles[entry.serviceType || ''] || entry.serviceType || 'värde'}
                                                                canCorrect={canCorrect && serviceType?.collectValue === true}
                                                                getUserName={getUserName}
                                                                onCorrected={onCorrected}
                                                            />
                                                        );
                                                    })()}
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                                        Inga aktiviteter hittades för denna byggnad i valt datumintervall.
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'visits' && (
                            <div>
                                {buildingVisits.length > 0 ? (
                                    <>
                                        <div style={{
                                            marginBottom: '20px',
                                            padding: '15px',
                                            backgroundColor: '#f0f8ff',
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            color: '#333'
                                        }}>
                                            <strong>Information:</strong> Denna tabell visar unika besök (ett per dag, baserat på senaste besöket den dagen).
                                        </div>
                                        <table style={{
                                            width: '100%',
                                            borderCollapse: 'collapse',
                                            fontSize: '14px'
                                        }}>
                                            <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                                            <tr style={{ borderBottom: '2px solid #eee' }}>
                                                <th style={{ textAlign: 'left', padding: '12px 8px', borderRight: '1px solid #f0f0f0' }}>Datum</th>
                                                <th style={{ textAlign: 'left', padding: '12px 8px' }}>Användare</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {buildingVisits.map((visit, index) => (
                                                <tr
                                                    key={visit.id}
                                                    style={{
                                                        borderBottom: '1px solid #f5f5f5',
                                                        backgroundColor: index % 2 === 1 ? '#f9f9f9' : 'transparent'
                                                    }}
                                                >
                                                    <td style={{ padding: '10px 8px', borderRight: '1px solid #f0f0f0' }}>
                                                        {formatDate(visit.dateString)}
                                                    </td>
                                                    <td style={{ padding: '10px 8px' }}>
                                                        {getUserName(visit.user)}
                                                    </td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </>
                                ) : (
                                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                                        Inga besök hittades för denna byggnad i valt datumintervall.
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BuildingLogsModal;